import Network from 'infrastructure/js/modules/network';
import * as formActionsHelper from '../Forms/formActionsHelper';
import { api as messageDialogApi } from '../MessageDialog/messageDialogActions.js';
import * as qplsService from '../../services/Qpls/qplsService.js';
import { formsNames } from '../../enums/formsNames';
import { getResponseValidationDetails } from 'infrastructure/js/components/Form/formHelper';
import {enumTypes, getEnumLabel, getEnumValue} from '../../utils/enumHelper';
import {createLabelHelper} from 'infrastructure/js/utils/labelHelper';
import DateTimeHelper from 'infrastructure/js/utils/dateTimeHelper';
import MaterialHelper from '../../utils/materialHelper';
import importFileService from '../../services/ImportFiles/importFilesService';
import {getFileTypeByCategoryBy} from '../../components/CoC/utils/cocHelper';
import {isLotAccessible} from '../../utils/assetHelper';

const config = {
  getSubmitMethod: () => null, //set null when override the formActionsHelper's submit() method
};

export const actionTypes = {
  ...formActionsHelper.getActionTypes(formsNames.COC_TEST_FORM),
};

const actions = {
  ...formActionsHelper.getActions(formsNames.COC_TEST_FORM),

};

export let api = {
  ...formActionsHelper.getApiActions(actions, config),


  fetchFormItem(dispatch, getState) {
    return function (cocTestId, cocId) {
      dispatch(actions.setBusy(true));

      let promise1 = qplsService.getCoCDetails(cocId);
      let promise2 = qplsService.getCoCTestDetails(cocTestId);

      return Promise.all([promise1, promise2]).then((allResults) => {

        dispatch(actions.setBusy(false));

        const invalidResponse = allResults.find(response => {
          return !Network.isResponseValid(response) || !response.data;
        });

        if (invalidResponse){
          console.error('Fetch CoC test details failed', invalidResponse);
          messageDialogApi.responseError(dispatch, getState)(invalidResponse);
          return null;
        }

        return _normalizeData(allResults[0]?.data, allResults[1]?.data);
      })
    }
  },

  submit(dispatch, getState) {
    return function (data, testId, callback) {
      dispatch(actions.setBusy(true));

      qplsService.saveCoCTest(data, testId).then((response) => {
        dispatch(actions.setBusy(false));

        let validations = getResponseValidationDetails(response);
        if (validations) {
          dispatch(actions.setValidationWarningsData(validations));
          return { success: false };
        }

        if (!Network.isResponseValid(response)) {
          console.error('Saving CoC test failed', response);
          messageDialogApi.responseError(dispatch, getState)(response);
          return { success: false };
        }

        callback?.();

        return { success: true };
      });
    };
  },

  uploadCocAttachment(dispatch, getState) {
    return function (file, data, setIndex, callback) {
      dispatch(actions.setBusy(true));

      importFileService.uploadAttachment(file, data).then((response) => {
        dispatch(actions.setBusy(false));

        if (!Network.isResponseValid(response) || !Network.isResponseValid(response.dataList?.[0])) {
          let failedResponse = !Network.isResponseValid(response) ? response : response.dataList?.[0];
          console.error('Uploading CoC attachment failed', failedResponse);
          messageDialogApi.responseError(dispatch, getState)(failedResponse);
          return { success: false };
        }

        let attachment = response.dataList?.[0]?.data?.attachment ?? null;
        if (attachment) {
          const {id, originalFileName, category, temporary, tempOwner, testResult} = attachment;

          const uploadedFile =  {
            file: file,
            id,
            name: originalFileName,
            temporary,
            type: getFileTypeByCategoryBy(category),
            tempOwner,
            testResult
          };

          callback?.(uploadedFile, setIndex);
        }
        return { success: true };
      });
    };
  },

  removeCocAttachment(dispatch, getState) {
    return function (file, data,setIndex, callback) {
      dispatch(actions.setBusy(true));

      importFileService.deleteAttachment(data).then((response) => {
        dispatch(actions.setBusy(false));

        if (!Network.isResponseValid(response)) {
          console.error('Removing CoC attachment failed', response);
          messageDialogApi.responseError(dispatch, getState)(response);
          return { success: false };
        }

        callback?.(file, setIndex);

        return { success: true };
      });
    };
  },

  getCocAttachmentUrl(dispatch, getState) {
    return function (file, setIndex, callback) {
      dispatch(actions.setBusy(true));

      qplsService.getCocAttachmentUrl(file.id).then((response) => {
        dispatch(actions.setBusy(false));

        if (!Network.isResponseValid(response)) {
          console.error('Getting CoC attachment URL failed', response);
          messageDialogApi.responseError(dispatch, getState)(response);
          return { success: false };
        }

        callback?.(response.data, file, setIndex);

        return { success: true };
      });
    };
  },

};

export let jsxActions = {
  ...formActionsHelper.getJsxActions(api),

  fetchFormItem(qplTestId, qplId) {
    return function (dispatch, getState) {
      return api.fetchFormItem(dispatch, getState)(qplTestId, qplId);
    };
  },

  submit(data, testId, callback) {
    return function (dispatch, getState) {
      return api.submit(dispatch, getState)(data, testId, callback);
    };
  },

  uploadCocAttachment(file, data, setIndex, callback) {
    return function (dispatch, getState) {
      return api.uploadCocAttachment(dispatch, getState)(file, data, setIndex,  callback);
    };
  },

  removeCocAttachment(file, data, setIndex, callback) {
    return function (dispatch, getState) {
      return api.removeCocAttachment(dispatch, getState)(file, data, setIndex, callback);
    };
  },

  getCocAttachmentUrl(file, setIndex, callback) {
    return function (dispatch, getState) {
      return api.getCocAttachmentUrl(dispatch, getState)(file, setIndex, callback);
    };
  },


};

/////////////////////////////////////////////////////////////////////////
// PRIVATE HELPERS

function _normalizeData(cocData, cocTestData) {
  return {
    cocTestData: buildTestData(cocData, cocTestData),
    cocSectionsData: buildSectionsData(cocTestData.sets, cocTestData.type),
    cocAttachmentsCounters: buildCounters(cocTestData.sets),
    cocAttachmentsFiles: buildAttachmentsFiles(cocTestData.sets)
  };
}

function getAdjacentIds(tests= [], testId) {

  const index = tests?.findIndex(item => item.id === testId);

  const previousId = index > 0 ? tests[index - 1]?.id : null;
  const nextId = (index !== -1 && index < tests.length - 1) ? tests[index + 1]?.id : null;

  return {
    previousId,
    nextId
  };
}

function buildTestData(cocData, cocTestData) {

  return {
    qplName: cocData.name,
    qplVersion: cocData.qplVersion,
    isViewOnly: !isLotAccessible(cocData.lotStatus),
    lotStatus: cocData.lotStatus,
    id: cocTestData.id,
    name: cocTestData.name,
    testResult: cocTestData.testResult,
    description: cocTestData.description,
    material: MaterialHelper.getMaterialFullLabel(cocTestData.material?.materialName, cocTestData.material?.businessId),
    type: getEnumLabel(enumTypes.TEST_TYPE)(cocTestData.type),
    lastUpdated: DateTimeHelper.DateTimeFormat(cocTestData?.lastUpdated?.epochDateTime * 1000),
    ...getAdjacentIds(cocData.tests, cocTestData.id),
  };
}

function buildSectionsData(sets, testType) {
  const allLabels = createLabelHelper('');
  return sets.map((set, index) => buildSection(set, index, testType, allLabels));
}

function buildSection(set, index, testType, allLabels) {

  const optionalFields = set.fields?.filter(f => f.isDisplayedInHeader).map(field => ({
    label: field.displayName ?? allLabels.get(field.labelKey),
    value: field.value,
  })) || [];

  const rawMaterialsFields = set.fields?.filter(f => !f.isDisplayedInHeader) || [];

  const isQualityTest = testType === getEnumValue(enumTypes.TEST_TYPE)('QUALITY');

  const rawMaterialsResultFieldsIds = isQualityTest ?
    rawMaterialsFields.map(field => field.id)
    :
    rawMaterialsFields.filter((field, index) =>  field.columnOrder % 2 === 0 )
                      .map(field => field.id);

  return {
    setName: set.name,
    setId: set.id,
    setNumber: set.setNumber,
    rawMaterialsTested: set.rawMaterialTested,
    hasComment: set.hasComment,
    comment: set.comment,
    passCriteria: isQualityTest ? '' : set.passCriteria,
    minimumValue: set.minimumValue,
    maximumValue: set.maximumValue,
    optionalFields,
    rawMaterialsFields: groupRawMaterials(rawMaterialsFields),
    rawMaterialsResultFieldsIds,
    attachments: set.attachments,
  };
}

function groupRawMaterials(fields) {
  if (!fields?.length) return [];

  const grouped = fields.reduce((acc, item) => {
    const key = item.rowOrder - 1;
    acc[key] = acc[key] || [];
    acc[key].push(item);
    return acc;
  }, {});

  return Object.values(grouped).map(group =>
    group.sort((a, b) => a.columnOrder - b.columnOrder)
  );
}

function buildAttachmentsCounters(set) {
  const attachments = [];

  const attachmentTypes = [
    { key: 'pdfsNumber', type: 'PDF', category: 'COC_PDF' },
    { key: 'graphsNumber', type: 'GRAPH', category: 'COC_GRAPH' },
    { key: 'csvNumber', type: 'CSV', category: 'COC_CSV' },
    { key: 'excelsNumber', type: 'EXCEL', category: 'COC_EXCEL' },
  ];

  for (const { key, type, category } of attachmentTypes) {
    const countTotal = set[key];
    if (countTotal) {
      attachments.push({
        type,
        attachmentsTotal: countTotal,
        attachmentsCount: set.attachments?.filter(item => item.category === category)?.length ?? 0,
      });
    }
  }

  return attachments;
}

function buildAttachedFiles(set) {
  let res = set?.attachments?.map(item => {
    const {id, originalFileName, category, temporary, testResult, tempOwner} = item;

    return {
      id,
      name: originalFileName,
      temporary,
      type: getFileTypeByCategoryBy(category),
      testResult,
      tempOwner
    };
  }) ?? [];
  return res;
}

function buildCounters(sets) {
  return sets.map((set, index) => buildAttachmentsCounters(set, index, ));
}

function buildAttachmentsFiles(sets) {
  return sets.map((set, index) => buildAttachedFiles(set, index, ));
}



